import React from 'react';
import {
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  Button,
  Badge,
  alpha
} from '@mui/material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import LinkIcon from '@mui/icons-material/Link';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import PushPinIcon from '@mui/icons-material/PushPin';
import FiberNewIcon from '@mui/icons-material/FiberNew';
import type { DriveResource } from '../types';
import { DRIVE_SECTION_LABEL_MAP } from '../constants';

interface DriveResourceCardProps {
  resource: DriveResource;
  onEdit: (resource: DriveResource) => void;
  onDelete: (resource: DriveResource) => void;
  canManage?: boolean;
}

const getResourceTypeIcon = (resource: DriveResource) => {
  switch (resource.resourceType) {
    case 'storage':
      return <InsertDriveFileIcon fontSize="small" sx={{ color: '#fff' }} />;
    case 'google-drive':
      return <FolderSharedIcon fontSize="small" sx={{ color: '#fff' }} />;
    default:
      return <LinkIcon fontSize="small" sx={{ color: '#fff' }} />;
  }
};

const getResourceTypeColor = (resourceType: DriveResource['resourceType']) => {
  switch (resourceType) {
    case 'storage':
      return { from: '#2196f3', to: '#42a5f5' }; // Blue gradient
    case 'google-drive':
      return { from: '#4caf50', to: '#66bb6a' }; // Green gradient
    default:
      return { from: '#9c27b0', to: '#ba68c8' }; // Purple gradient
  }
};

const getResourceTypeLabel = (resourceType: DriveResource['resourceType']) => {
  switch (resourceType) {
    case 'storage':
      return 'Storage';
    case 'google-drive':
      return 'Google Drive';
    default:
      return 'External Link';
  }
};

const isNewResource = (date?: Date) => {
  if (!date) return false;
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  return date > sevenDaysAgo;
};

const formatBytes = (bytes: number) => {
  if (!bytes) return 'Unknown size';
  const kilobyte = 1024;
  const megabyte = kilobyte * 1024;
  const gigabyte = megabyte * 1024;
  if (bytes >= gigabyte) return `${(bytes / gigabyte).toFixed(2)} GB`;
  if (bytes >= megabyte) return `${(bytes / megabyte).toFixed(1)} MB`;
  if (bytes >= kilobyte) return `${(bytes / kilobyte).toFixed(1)} KB`;
  return `${bytes} bytes`;
};

const getPrimaryActionLabel = (resource: DriveResource) => {
  switch (resource.resourceType) {
    case 'storage':
      return 'Download';
    case 'google-drive':
      return 'Open Drive';
    default:
      return 'Open Link';
  }
};

const getPrimaryActionHref = (resource: DriveResource) => {
  if (resource.resourceType === 'storage') {
    return resource.downloadUrl || undefined;
  }
  return resource.externalUrl || undefined;
};

const getPrimaryActionIcon = (resource: DriveResource) => {
  if (resource.resourceType === 'storage') {
    return <CloudDownloadIcon fontSize="small" />;
  }
  return <OpenInNewIcon fontSize="small" />;
};

const formatDate = (date?: Date) => {
  if (!date) return 'Not available';
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

export const DriveResourceCard: React.FC<DriveResourceCardProps> = ({ resource, onEdit, onDelete, canManage = true }) => {
  const actionHref = getPrimaryActionHref(resource);
  const actionLabel = getPrimaryActionLabel(resource);
  const typeColors = getResourceTypeColor(resource.resourceType);
  const isPinned = (resource.pinWeight ?? 0) > 0;
  const isNew = isNewResource(resource.createdAt);

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        height: '100%',
        position: 'relative',
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'scale(1.02)',
          boxShadow: (theme) => `0 8px 24px ${alpha(typeColors.from, 0.2)}`,
          borderColor: typeColors.from,
        }
      }}
    >
      {/* Pin Badge - Top Right */}
      {isPinned && (
        <Box
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            zIndex: 2,
          }}
        >
          <Tooltip title={`Pinned (weight: ${resource.pinWeight})`}>
            <Box
              sx={{
                background: 'linear-gradient(135deg, #ffd700 0%, #ffed4e 100%)',
                borderRadius: '50%',
                width: 32,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(255, 215, 0, 0.4)',
              }}
            >
              <PushPinIcon sx={{ fontSize: 18, color: '#fff' }} />
            </Box>
          </Tooltip>
        </Box>
      )}

      {/* Resource Type Badge - Top Left */}
      <Box
        sx={{
          position: 'absolute',
          top: 12,
          left: 12,
          zIndex: 1,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            icon={getResourceTypeIcon(resource)}
            label={getResourceTypeLabel(resource.resourceType)}
            size="small"
            sx={{
              background: `linear-gradient(135deg, ${typeColors.from} 0%, ${typeColors.to} 100%)`,
              color: '#fff',
              fontWeight: 600,
              fontSize: '0.75rem',
              height: 28,
              '& .MuiChip-icon': {
                marginLeft: 1,
              }
            }}
          />
          {/* NEW Badge - Next to Resource Type */}
          {isNew && (
            <Chip
              icon={<FiberNewIcon />}
              label="NEW"
              size="small"
              sx={{
                background: 'linear-gradient(135deg, #ff9800 0%, #ffb74d 100%)',
                color: '#fff',
                fontWeight: 700,
                fontSize: '0.7rem',
                height: 24,
                boxShadow: '0 2px 8px rgba(255, 152, 0, 0.4)',
                '& .MuiChip-icon': {
                  color: '#fff',
                  fontSize: 14,
                }
              }}
            />
          )}
        </Stack>
      </Box>

      <CardHeader
        sx={{ pb: 1.5, pt: 6 }}
        title={
          <Typography variant="subtitle1" fontWeight={700} sx={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            lineHeight: 1.3,
            minHeight: '2.6em'
          }}>
            {resource.title}
          </Typography>
        }
      />

      <CardContent sx={{ pt: 0, pb: 2 }}>
        {resource.description ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 2,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              position: 'relative',
              '&::after': resource.description.length > 80 ? {
                content: '""',
                position: 'absolute',
                bottom: 0,
                right: 0,
                width: '30%',
                height: '1.2em',
                background: (theme) => `linear-gradient(to right, transparent, ${theme.palette.background.paper})`,
              } : {}
            }}
          >
            {resource.description}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic', opacity: 0.6 }}>
            No description provided.
          </Typography>
        )}

        <Stack spacing={1.5}>
          {resource.resourceType === 'storage' && (
            <Box sx={{
              bgcolor: (theme) => alpha(typeColors.from, 0.08),
              px: 1.5,
              py: 0.75,
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: (theme) => alpha(typeColors.from, 0.2),
            }}>
              <Typography variant="caption" fontWeight={600} sx={{ color: typeColors.from }}>
                {resource.fileName} · {formatBytes(resource.fileSize)}
              </Typography>
            </Box>
          )}

          {resource.resourceType !== 'storage' && resource.externalUrl && (
            <Tooltip title={resource.externalUrl} placement="top-start">
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  px: 1,
                  py: 0.5,
                  bgcolor: (theme) => alpha(typeColors.from, 0.05),
                  borderRadius: 1,
                }}
              >
                🔗 {resource.externalUrl}
              </Typography>
            </Tooltip>
          )}

          <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 0.5 }}>
            <CalendarTodayIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
            <Typography variant="caption" color="text.secondary" fontWeight={500}>
              Updated {formatDate(resource.lastUpdatedAt)}
            </Typography>
          </Stack>

          {resource.tags && resource.tags.length > 0 && (
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {resource.tags.slice(0, 3).map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  sx={{
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                    color: 'primary.main',
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    height: 22,
                  }}
                />
              ))}
              {resource.tags.length > 3 && (
                <Chip
                  label={`+${resource.tags.length - 3}`}
                  size="small"
                  sx={{
                    bgcolor: (theme) => alpha(theme.palette.text.secondary, 0.1),
                    color: 'text.secondary',
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    height: 22,
                  }}
                />
              )}
            </Stack>
          )}
        </Stack>
      </CardContent>

      <CardActions sx={{ px: 2.5, pb: 2.5, pt: 0, justifyContent: 'space-between' }}>
        {actionHref ? (
          <Button
            size="small"
            variant="contained"
            component="a"
            href={actionHref}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={getPrimaryActionIcon(resource)}
            sx={{
              background: `linear-gradient(135deg, ${typeColors.from} 0%, ${typeColors.to} 100%)`,
              color: '#fff',
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: `0 2px 8px ${alpha(typeColors.from, 0.3)}`,
              '&:hover': {
                background: `linear-gradient(135deg, ${typeColors.from} 0%, ${typeColors.to} 100%)`,
                boxShadow: `0 4px 12px ${alpha(typeColors.from, 0.4)}`,
                transform: 'translateY(-1px)',
              },
              transition: 'all 0.2s',
            }}
          >
            {actionLabel}
          </Button>
        ) : (
          <Button
            size="small"
            variant="outlined"
            startIcon={getPrimaryActionIcon(resource)}
            disabled
            sx={{ textTransform: 'none' }}
          >
            {actionLabel}
          </Button>
        )}
        {canManage && (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Edit resource">
              <IconButton
                size="small"
                onClick={() => onEdit(resource)}
                sx={{
                  color: 'text.secondary',
                  '&:hover': {
                    color: 'primary.main',
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                    transform: 'rotate(15deg)',
                  },
                  transition: 'all 0.2s',
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete resource">
              <IconButton
                size="small"
                onClick={() => onDelete(resource)}
                sx={{
                  color: 'text.secondary',
                  '&:hover': {
                    color: 'error.main',
                    bgcolor: (theme) => alpha(theme.palette.error.main, 0.1),
                    animation: 'shake 0.3s',
                  },
                  '@keyframes shake': {
                    '0%, 100%': { transform: 'rotate(0deg)' },
                    '25%': { transform: 'rotate(-5deg)' },
                    '75%': { transform: 'rotate(5deg)' },
                  },
                  transition: 'all 0.2s',
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
      </CardActions>
    </Card>
  );
};

export default DriveResourceCard;
